import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  LEAD_STATUSES,
  LEAD_STATUS_LABELS,
  LEGAL_BASIS_LABELS,
  LEGAL_BASIS_VALUES,
  EMAIL_VERIFICATION_LABELS,
  type LegalBasis,
} from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type {
  Account,
  Lead,
  LeadCampaignEnrollment,
  Pipeline,
  PipelineBoardStage,
} from '@/api/types';
import { ActivityComposer } from '@/features/activities/ActivityComposer';
import { ActivityTimeline } from '@/features/activities/ActivityTimeline';
import { labelEnrollmentStatus } from '@/features/campaigns/enrollmentLabels';
import { StatusBadge } from '@/components/StatusBadge/StatusBadge';
import { Button } from '@/components/ui/Button';
import { Field, TextInput } from '@/components/ui/Field';
import { CompanyAutocomplete } from './CompanyAutocomplete';
import { ErrorState, LoadingState } from '@/components/StateMessage/StateMessage';
import { LeadComposeMail } from './LeadComposeMail';
import { LeadAudiencesSection } from './LeadAudiencesSection';
import {
  RecordDetailShell,
  recordDetailShellStyles as shell,
} from '@/components/workspace/RecordDetailShell';
import { COMMON_IANA_TIMEZONES } from '@/constants/ianaTimezones';
import { TraceLink } from '@/features/traces/TraceLink';
import traceStyles from '@/features/traces/traces.module.css';
import styles from './Leads.module.css';

type LeadDetailTab = 'overview' | 'company' | 'outreach' | 'compliance' | 'activity';

const DETAIL_TABS: { id: LeadDetailTab; label: string }[] = [
  { id: 'overview', label: 'Overzicht' },
  { id: 'company', label: 'Bedrijf' },
  { id: 'outreach', label: 'Outreach' },
  { id: 'compliance', label: 'Compliance' },
  { id: 'activity', label: 'Activiteit' },
];

function leadDisplayName(lead: Lead): string {
  return lead.contact_name?.trim() || lead.company?.trim() || lead.email?.trim() || 'Lead';
}

function leadInitials(lead: Lead): string {
  const base = lead.contact_name?.trim() || lead.company?.trim() || lead.email?.trim() || '?';
  const parts = base.split(/\s+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0]![0]! + parts[1]![0]!).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

const SCAN_OUTCOME_LABELS: Record<string, string> = {
  email_set: 'E-mail ingevuld',
  email_unchanged: 'E-mail ongewijzigd (al aanwezig)',
  email_duplicate: 'Gevonden e-mail hoort bij andere lead',
  no_website: 'Geen website',
  blocklisted: 'Domein op do-not-scrape',
  not_found: 'Geen e-mail op website gevonden',
  failed: 'Scan mislukt',
};

type LeadPatch = Partial<
  Pick<
    Lead,
    | 'status'
    | 'job_title'
    | 'linkedin_url'
    | 'legal_basis'
    | 'do_not_contact'
    | 'account_id'
    | 'tags'
    | 'company'
    | 'website'
    | 'phone'
    | 'notes'
    | 'custom_fields'
    | 'timezone'
  >
>;

export function LeadDetailPanel({ leadId, onClose }: { leadId: string; onClose: () => void }) {
  const qc = useQueryClient();

  const [replaceEmailOnScan, setReplaceEmailOnScan] = useState(false);
  const [companyDraft, setCompanyDraft] = useState('');
  const [tab, setTab] = useState<LeadDetailTab>('overview');
  const [enrollSimulateOut, setEnrollSimulateOut] = useState<string | null>(null);

  const leadQ = useQuery({
    queryKey: ['lead', leadId],
    queryFn: () => apiFetch<{ lead: Lead }>(`/api/leads/${leadId}`),
    refetchInterval: (query) => {
      const st = query.state.data?.lead.source_meta?.website_email_scan_status;
      return st === 'queued' || st === 'running' ? 4000 : false;
    },
  });

  const lastRecentRef = useRef<string | null>(null);
  useEffect(() => {
    const lead = leadQ.data?.lead;
    if (lead) setCompanyDraft(lead.company ?? '');
  }, [leadQ.data?.lead?.id, leadQ.data?.lead?.company]);

  useEffect(() => {
    setTab('overview');
  }, [leadId]);

  useEffect(() => {
    const lead = leadQ.data?.lead;
    if (!lead || lastRecentRef.current === lead.id) return;
    lastRecentRef.current = lead.id;
    const title = lead.contact_name ?? lead.company ?? lead.email ?? 'Lead';
    void apiFetch('/api/platform/recent-records', {
      method: 'POST',
      body: JSON.stringify({
        project_id: lead.project_id,
        record_type: 'lead',
        record_id: lead.id,
        title,
      }),
    }).then(() => void qc.invalidateQueries({ queryKey: ['recent-records'] }));
  }, [leadQ.data?.lead, qc]);

  const dupQ = useQuery({
    queryKey: ['lead-duplicates', leadId],
    queryFn: () =>
      apiFetch<{
        duplicates: {
          id: string;
          contact_name: string | null;
          email: string | null;
          company: string | null;
        }[];
      }>(`/api/leads/${leadId}/duplicates`),
  });

  const accountsQ = useQuery({
    queryKey: ['accounts', leadQ.data?.lead.project_id],
    enabled: Boolean(leadQ.data?.lead.project_id),
    queryFn: () => {
      const pid = leadQ.data!.lead.project_id;
      return apiFetch<{ accounts: Account[] }>(`/api/accounts?project_id=${pid}`);
    },
  });

  const enrollQ = useQuery({
    queryKey: ['lead-enrollments', leadId],
    queryFn: () =>
      apiFetch<{ enrollments: LeadCampaignEnrollment[] }>(
        `/api/leads/${leadId}/campaign-enrollments`,
      ),
  });

  const latestTraceQ = useQuery({
    queryKey: ['lead-latest-trace', leadId],
    enabled: Boolean(leadQ.data?.lead.project_id),
    queryFn: () =>
      apiFetch<{
        trace: { trace_id: string; created_at: string; trigger_event_type: string | null } | null;
      }>(`/api/leads/${leadId}/latest-hub-trace`),
  });

  const enrollSimulateM = useMutation({
    mutationFn: () => {
      const pid = leadQ.data!.lead.project_id;
      return apiFetch<{
        action: string;
        matched_policy_id: string | null;
        reasons: string[];
        policy_trail: { policy_id: string; name: string; skipped_reason?: string }[];
      }>('/api/enroll-policies/simulate', {
        method: 'POST',
        body: JSON.stringify({ project_id: pid, lead_id: leadId, trigger_kind: 'inbound_request' }),
      });
    },
    onSuccess: (data) => {
      setEnrollSimulateOut(
        [
          `Actie: ${data.action}`,
          data.matched_policy_id ? `Policy: ${data.matched_policy_id}` : null,
          `Redenen: ${data.reasons.join(', ')}`,
          data.policy_trail.length
            ? `Trail: ${data.policy_trail.map((t) => `${t.name}${t.skipped_reason ? ` (${t.skipped_reason})` : ''}`).join(' → ')}`
            : null,
        ]
          .filter(Boolean)
          .join('\n'),
      );
    },
  });

  const patchLead = useMutation({
    mutationFn: (body: LeadPatch) =>
      apiFetch(`/api/leads/${leadId}`, { method: 'PATCH', body: JSON.stringify(body) }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['leads'] });
      void qc.invalidateQueries({ queryKey: ['lead', leadId] });
      void qc.invalidateQueries({ queryKey: ['timeline'] });
    },
  });

  const scanWebsiteEmail = useMutation({
    mutationFn: () =>
      apiFetch(`/api/leads/${leadId}/scan-website-email`, {
        method: 'POST',
        body: JSON.stringify({ replace_existing: replaceEmailOnScan }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead', leadId] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const gdprErase = useMutation({
    mutationFn: () =>
      apiFetch(`/api/leads/${leadId}/gdpr-erase`, {
        method: 'POST',
        body: JSON.stringify({ confirm: true }),
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['lead', leadId] });
      void qc.invalidateQueries({ queryKey: ['leads'] });
    },
  });

  const promoteDeal = useMutation({
    mutationFn: async () => {
      const lead = leadQ.data!.lead;
      const { pipelines } = await apiFetch<{ pipelines: Pipeline[] }>(
        `/api/pipelines?project_id=${lead.project_id}`,
      );
      const pipeline = pipelines.find((p) => p.is_default) ?? pipelines[0];
      if (!pipeline) throw new Error('Geen pipeline');
      const board = await apiFetch<{ stages: PipelineBoardStage[] }>(
        `/api/pipelines/${pipeline.id}/board?project_id=${lead.project_id}`,
      );
      const stage = board.stages.find((s) => s.outcome === 'open');
      if (!stage) throw new Error('Geen open stage');
      const name = lead.company ?? lead.contact_name ?? lead.email ?? 'Deal';
      return apiFetch('/api/deals', {
        method: 'POST',
        body: JSON.stringify({
          project_id: lead.project_id,
          pipeline_id: pipeline.id,
          stage_id: stage.id,
          name,
          primary_lead_id: lead.id,
          account_id: lead.account_id ?? null,
          source: 'lead',
        }),
      });
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['pipeline-board'] }),
  });

  if (leadQ.isLoading) {
    return (
      <RecordDetailShell ariaLabel="Lead detail">
        <LoadingState label="Lead laden…" />
      </RecordDetailShell>
    );
  }
  if (leadQ.isError || !leadQ.data) {
    return (
      <RecordDetailShell ariaLabel="Lead detail">
        <ErrorState message="Lead niet gevonden" />
      </RecordDetailShell>
    );
  }

  const lead = leadQ.data.lead;
  const tagsStr = (lead.tags ?? []).join(', ');
  const customFields = lead.custom_fields ?? {};
  const industryStr = typeof customFields.industry === 'string' ? customFields.industry : '';
  const companySizeStr =
    typeof customFields.company_size === 'string' ? customFields.company_size : '';
  const displayName = leadDisplayName(lead);
  const enrollments = enrollQ.data?.enrollments ?? [];
  const duplicates = dupQ.data?.duplicates ?? [];

  return (
    <RecordDetailShell ariaLabel="Lead detail">
      <RecordDetailShell.Header
        identity={
          <div className={shell.avatar} aria-hidden>
            {leadInitials(lead)}
          </div>
        }
        title={displayName}
        subtitle={lead.company && lead.contact_name ? lead.company : undefined}
        badges={
          <>
            <StatusBadge status={lead.status} />
            {lead.do_not_contact ? <span className={shell.badgeWarn}>DNC</span> : null}
            {lead.anonymized_at ? <span className={shell.badgeMuted}>AVG</span> : null}
          </>
        }
        onClose={onClose}
      />

      <RecordDetailShell.QuickActions>
        {lead.email ? (
          <a className={shell.quickLink} href={`mailto:${lead.email}`}>
            E-mail
          </a>
        ) : (
          <span className={shell.quickDisabled}>Geen e-mail</span>
        )}
        {lead.phone ? (
          <a className={shell.quickLink} href={`tel:${lead.phone}`}>
            Bellen
          </a>
        ) : null}
        {lead.website ? (
          <a
            className={shell.quickLink}
            href={lead.website.startsWith('http') ? lead.website : `https://${lead.website}`}
            target="_blank"
            rel="noreferrer"
          >
            Website
          </a>
        ) : null}
        <Button
          variant="secondary"
          disabled={promoteDeal.isPending}
          onClick={() => promoteDeal.mutate()}
        >
          Deal
        </Button>
        {lead.website && !lead.anonymized_at ? (
          <Button
            variant="secondary"
            disabled={
              scanWebsiteEmail.isPending ||
              lead.source_meta?.website_email_scan_status === 'queued' ||
              lead.source_meta?.website_email_scan_status === 'running'
            }
            onClick={() => {
              setTab('outreach');
              scanWebsiteEmail.mutate();
            }}
          >
            {lead.source_meta?.website_email_scan_status === 'running' ||
            lead.source_meta?.website_email_scan_status === 'queued'
              ? 'Scannen…'
              : 'E-mail zoeken'}
          </Button>
        ) : null}
      </RecordDetailShell.QuickActions>
      {promoteDeal.isError ? (
        <p className={styles.panelInlineError}>{(promoteDeal.error as Error).message}</p>
      ) : null}

      <RecordDetailShell.Tabs
        tabs={DETAIL_TABS}
        activeTab={tab}
        onTabChange={(id) => setTab(id as LeadDetailTab)}
        ariaLabel="Lead secties"
      />

      <RecordDetailShell.Body>
        {tab === 'overview' ? (
          <>
            <section className={styles.panelSection}>
              <h3 className={styles.panelSectionTitle}>Contact</h3>
              <dl className={styles.metaGrid}>
                <div>
                  <dt>E-mail</dt>
                  <dd>{lead.email ?? '—'}</dd>
                </div>
                <div>
                  <dt>Telefoon</dt>
                  <dd>{lead.phone ?? '—'}</dd>
                </div>
                <div>
                  <dt>Functie</dt>
                  <dd>{lead.job_title ?? '—'}</dd>
                </div>
                <div>
                  <dt>Bron</dt>
                  <dd>{lead.source_code ?? '—'}</dd>
                </div>
              </dl>
              {lead.email_verification_status &&
              lead.email_verification_status !== 'unknown' &&
              lead.email_verification_status in EMAIL_VERIFICATION_LABELS ? (
                <p className={styles.muted}>
                  Verificatie:{' '}
                  {
                    EMAIL_VERIFICATION_LABELS[
                      lead.email_verification_status as keyof typeof EMAIL_VERIFICATION_LABELS
                    ]
                  }
                </p>
              ) : null}
            </section>

            {latestTraceQ.data?.trace ? (
              <section className={styles.panelSection}>
                <h3 className={styles.panelSectionTitle}>Hub trace</h3>
                <span className={traceStyles.traceChip}>
                  {latestTraceQ.data.trace.trigger_event_type ?? 'event'} ·{' '}
                  {new Date(latestTraceQ.data.trace.created_at).toLocaleString('nl-NL')} ·{' '}
                  <TraceLink traceId={latestTraceQ.data.trace.trace_id} label="Open trace" />
                </span>
              </section>
            ) : null}

            <section className={styles.panelSection}>
              <h3 className={styles.panelSectionTitle}>Attributie</h3>
              <div className={styles.metaChips}>
                {lead.first_touch_source ? (
                  <span className={styles.metaChip}>First-touch: {lead.first_touch_source}</span>
                ) : null}
                {lead.utm_campaign ? (
                  <span className={styles.metaChip}>
                    UTM: {[lead.utm_source, lead.utm_medium, lead.utm_campaign]
                      .filter(Boolean)
                      .join(' / ')}
                  </span>
                ) : null}
                {lead.source_url ? (
                  <a className={styles.metaChipLink} href={lead.source_url} target="_blank" rel="noreferrer">
                    Bron-URL
                  </a>
                ) : null}
              </div>
            </section>

            {duplicates.length ? (
              <section className={`${styles.panelSection} ${styles.panelAlert}`}>
                <h3 className={styles.panelSectionTitle}>Mogelijke duplicaten</h3>
                <ul className={styles.duplicateList}>
                  {duplicates.map((d) => (
                    <li key={d.id}>
                      {d.contact_name ?? d.email} {d.company ? `· ${d.company}` : ''}
                    </li>
                  ))}
                </ul>
              </section>
            ) : null}

            <section className={styles.panelSection}>
              <h3 className={styles.panelSectionTitle}>Campagnes</h3>
              {enrollQ.isLoading ? (
                <LoadingState label="Campagnes laden…" />
              ) : !enrollments.length ? (
                <p className={styles.muted}>Geen actieve inschrijvingen.</p>
              ) : (
                <ul className={styles.enrollmentCards}>
                  {enrollments.slice(0, 3).map((e) => (
                    <li key={e.id} className={styles.enrollmentCard}>
                      <strong>{e.campaign_name}</strong>
                      <span>{labelEnrollmentStatus(e.status)}</span>
                      <span className={styles.muted}>Stap {e.step_index + 1}</span>
                    </li>
                  ))}
                </ul>
              )}
              {enrollments.length > 3 ? (
                <button type="button" className={styles.panelLinkBtn} onClick={() => setTab('outreach')}>
                  Alle campagnes ({enrollments.length})
                </button>
              ) : null}
            </section>

            <section className={styles.panelSection}>
              <h3 className={styles.panelSectionTitle}>Recente touchpoints</h3>
              <ActivityTimeline projectId={lead.project_id} leadId={lead.id} limit={12} />
            </section>
          </>
        ) : null}

        {tab === 'company' ? (
          <>
            <h3 className={styles.formSectionTitle}>Bedrijfsprofiel</h3>
      <Field label="Bedrijfsnaam">
        <CompanyAutocomplete
          projectId={lead.project_id}
          value={companyDraft}
          onValueChange={setCompanyDraft}
          onBlur={() => {
            const v = companyDraft.trim() || null;
            if (v !== (lead.company ?? null)) {
              patchLead.mutate({ company: v, account_id: null });
            }
          }}
          onSelect={(sel) => {
            setCompanyDraft(sel.name);
            patchLead.mutate({
              company: sel.name,
              account_id: sel.account_id,
              website: sel.website ?? lead.website,
              custom_fields: {
                ...customFields,
                ...(sel.industry ? { industry: sel.industry } : {}),
                ...(sel.company_size ? { company_size: sel.company_size } : {}),
              },
            });
          }}
        />
      </Field>
      <Field label="Website">
        <TextInput
          key={`web-${lead.updated_at}`}
          defaultValue={lead.website ?? ''}
          onBlur={(e) => {
            const v = e.target.value.trim() || null;
            if (v !== (lead.website ?? null)) patchLead.mutate({ website: v });
          }}
        />
      </Field>
      <Field label="Branche">
        <TextInput
          key={`ind-${lead.updated_at}`}
          defaultValue={industryStr}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== industryStr) {
              patchLead.mutate({
                custom_fields: { ...customFields, industry: v || undefined },
              });
            }
          }}
        />
      </Field>
      <Field label="Bedrijfsgrootte">
        <TextInput
          key={`size-${lead.updated_at}`}
          defaultValue={companySizeStr}
          onBlur={(e) => {
            const v = e.target.value.trim();
            if (v !== companySizeStr) {
              patchLead.mutate({
                custom_fields: { ...customFields, company_size: v || undefined },
              });
            }
          }}
        />
      </Field>
            <Field label="Functie">
              <TextInput
                key={`job-${lead.updated_at}`}
                defaultValue={lead.job_title ?? ''}
                onBlur={(e) => {
                  const v = e.target.value.trim() || null;
                  if (v !== (lead.job_title ?? null)) patchLead.mutate({ job_title: v });
                }}
              />
            </Field>
            <Field label="LinkedIn">
              <TextInput
                key={`li-${lead.updated_at}`}
                defaultValue={lead.linkedin_url ?? ''}
                onBlur={(e) => {
                  const v = e.target.value.trim() || null;
                  if (v !== (lead.linkedin_url ?? null)) patchLead.mutate({ linkedin_url: v });
                }}
              />
            </Field>
            {lead.account_id ? (
              <p className={styles.muted}>
                Gekoppeld account:{' '}
                {(accountsQ.data?.accounts ?? []).find((a) => a.id === lead.account_id)?.name ??
                  lead.account_id.slice(0, 8)}
              </p>
            ) : null}
          </>
        ) : null}

        {tab === 'outreach' ? (
          <>
            {lead.email ? <LeadComposeMail leadId={lead.id} leadEmail={lead.email} /> : null}
            {lead.website && !lead.anonymized_at ? (
              <section className={styles.panelSection}>
                <h3 className={styles.panelSectionTitle}>Website e-mail scan</h3>
                <Button
                  variant="secondary"
                  disabled={
                    scanWebsiteEmail.isPending ||
                    lead.source_meta?.website_email_scan_status === 'queued' ||
                    lead.source_meta?.website_email_scan_status === 'running'
                  }
                  onClick={() => scanWebsiteEmail.mutate()}
                >
                  {lead.source_meta?.website_email_scan_status === 'running' ||
                  lead.source_meta?.website_email_scan_status === 'queued'
                    ? 'Website scannen…'
                    : 'Website opnieuw scannen (e-mail)'}
                </Button>
                {lead.email ? (
                  <label className={styles.muted}>
                    <input
                      type="checkbox"
                      checked={replaceEmailOnScan}
                      onChange={(e) => setReplaceEmailOnScan(e.target.checked)}
                    />{' '}
                    Vervang bestaande e-mail als andere gevonden wordt
                  </label>
                ) : null}
                {lead.source_meta?.website_email_scan_outcome ? (
                  <p className={styles.muted}>
                    Laatste scan:{' '}
                    {SCAN_OUTCOME_LABELS[lead.source_meta.website_email_scan_outcome] ??
                      lead.source_meta.website_email_scan_outcome}
                    {lead.source_meta.website_email_scan_at
                      ? ` · ${new Date(lead.source_meta.website_email_scan_at).toLocaleString()}`
                      : ''}
                  </p>
                ) : null}
                {scanWebsiteEmail.isError ? (
                  <p className={styles.panelInlineError}>
                    {(scanWebsiteEmail.error as Error).message}
                  </p>
                ) : null}
              </section>
            ) : null}
            <LeadAudiencesSection leadId={lead.id} projectId={lead.project_id} />
            <section className={styles.panelSection}>
              <h3 className={styles.panelSectionTitle}>Enroll policies (simulate)</h3>
              <p className={styles.muted}>
                Dry-run: welke policy zou deze lead inschrijven bij inbound (geen wijzigingen).
              </p>
              <Button
                type="button"
                variant="secondary"
                disabled={enrollSimulateM.isPending}
                onClick={() => enrollSimulateM.mutate()}
              >
                Test enroll policies
              </Button>
              {enrollSimulateOut ? (
                <pre className={styles.muted} style={{ whiteSpace: 'pre-wrap', fontSize: '0.85rem' }}>
                  {enrollSimulateOut}
                </pre>
              ) : null}
            </section>
            <section className={styles.panelSection}>
              <h3 className={styles.panelSectionTitle}>Campagnes</h3>
              {enrollQ.isLoading ? (
                <LoadingState label="Campagnes laden…" />
              ) : !enrollments.length ? (
                <p className={styles.muted}>Geen campagne-inschrijvingen.</p>
              ) : (
                <ul className={styles.enrollmentList}>
                  {enrollments.map((e) => (
                    <li key={e.id}>
                      <strong>{e.campaign_name}</strong> — {labelEnrollmentStatus(e.status)}
                      <div className={styles.muted}>Stap {e.step_index + 1}</div>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          </>
        ) : null}

        {tab === 'compliance' ? (
          <>
            <Field label="Status">
              <select
                className={styles.filterInput}
                value={lead.status}
                onChange={(e) => patchLead.mutate({ status: e.target.value })}
              >
                {LEAD_STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {LEAD_STATUS_LABELS[s]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Rechtsgrond">
              <select
                className={styles.filterInput}
                value={lead.legal_basis ?? ''}
                onChange={(e) =>
                  patchLead.mutate({
                    legal_basis: e.target.value ? (e.target.value as LegalBasis) : null,
                  })
                }
              >
                <option value="">—</option>
                {LEGAL_BASIS_VALUES.map((v) => (
                  <option key={v} value={v}>
                    {LEGAL_BASIS_LABELS[v]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Tags (komma-gescheiden)">
              <TextInput
                defaultValue={tagsStr}
                onBlur={(e) => {
                  const tags = e.target.value
                    .split(',')
                    .map((t) => t.trim())
                    .filter(Boolean);
                  if (tags.join(',') !== tagsStr) patchLead.mutate({ tags });
                }}
              />
            </Field>
            <Field label="Timezone (IANA, optioneel)">
              <TextInput
                key={`tz-${lead.updated_at}`}
                list="mh-lead-timezones"
                defaultValue={lead.timezone ?? ''}
                placeholder="Europe/Amsterdam"
                onBlur={(e) => {
                  const v = e.target.value.trim() || null;
                  if (v !== (lead.timezone ?? null)) patchLead.mutate({ timezone: v });
                }}
              />
              <datalist id="mh-lead-timezones">
                {COMMON_IANA_TIMEZONES.map((z) => (
                  <option key={z} value={z} />
                ))}
              </datalist>
              <p className={styles.muted}>
                Lead-timezone wint op project-default bij send window (campagnes).
              </p>
            </Field>
            <label className={styles.panelCheckbox}>
              <input
                type="checkbox"
                checked={Boolean(lead.do_not_contact)}
                onChange={(e) => patchLead.mutate({ do_not_contact: e.target.checked })}
              />
              Niet contacteren (DNC) — geen outbound mail/campagnes
            </label>
            {lead.anonymized_at ? (
              <p className={styles.muted}>
                <strong>AVG-geanonimiseerd</strong> (
                {new Date(lead.anonymized_at).toLocaleDateString()})
              </p>
            ) : null}
            {!lead.anonymized_at ? (
              <Button
                variant="ghost"
                disabled={gdprErase.isPending}
                onClick={() => {
                  if (window.confirm('Lead AVG-geanonimiseerd? Dit kan niet ongedaan.')) {
                    gdprErase.mutate();
                  }
                }}
              >
                AVG anonimiseren
              </Button>
            ) : null}
          </>
        ) : null}

        {tab === 'activity' ? (
          <>
            <ActivityComposer projectId={lead.project_id} leadId={lead.id} />
            <ActivityTimeline projectId={lead.project_id} leadId={lead.id} />
          </>
        ) : null}
      </RecordDetailShell.Body>
    </RecordDetailShell>
  );
}
